import { HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NGXLoggerServerService } from 'ngx-logger';
import { Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { AuthHeadersService } from './auth-headers.service';
import { BaseUrlService } from './base-url.service';

/**
 * An extension of NGXLoggerServerService that adds authentication headers to log on server requests.
 *
 * This service reactively observes the AuthHeadersService and automatically uses the current auth headers.
 * The AuthHeadersService acts as a mediator to avoid circular dependencies.
 */
@Injectable()
export class AuthenticatedLoggerServerService extends NGXLoggerServerService {
  private readonly authHeadersService = inject(AuthHeadersService);
  private readonly baseUrlService = inject(BaseUrlService);

  private readonly authHeaders$ = toObservable(this.authHeadersService.authHeaders);

  static readonly loggingPath = 'api/customer-data-frontend/logs';

  protected override alterHttpRequest(httpRequest: HttpRequest<unknown>): HttpRequest<unknown> {
    const baseUrl = this.baseUrlService.baseUrlSetByInput();
    const url = new URL(AuthenticatedLoggerServerService.loggingPath, baseUrl ?? location.origin).href;

    return httpRequest.clone({
      url,
      setHeaders: Object.fromEntries(this.authHeadersService.authHeaders() ?? []),
    });
  }

  protected override logOnServer(
    url: string,
    logContent: unknown,
    options: {
      headers?: HttpHeaders;
      reportProgress?: boolean;
      params?: HttpParams;
      responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
      withCredentials?: boolean;
    },
  ): Observable<unknown> {
    // Wait until the auth header is available before sending the log
    return this.authHeaders$.pipe(
      filter((h) => !!h),
      take(1),
      switchMap(() => super.logOnServer(url, logContent, options)),
    );
  }
}
